import React, { useState } from 'react';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import applicationService from '@/shared/services/applicationService';
import ActionBar from '@/components/common/ActionBar';

/**
 * Deletes a draft application after a confirmation. Only a draft: the server
 * refuses anything submitted, which is withdrawn instead.
 */
function DeleteDraftButton({
    applicationId,
    courseLabel,
    onDeleted,
    size,
    className,
}) {
    const [show, setShow] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState(null);
    const titleId = `delete-draft-title-${applicationId}`;

    const close = () => {
        if (deleting) return;
        setShow(false);
        setError(null);
    };

    const confirm = async () => {
        setDeleting(true);
        setError(null);
        try {
            await applicationService.deleteApplication(applicationId);
            setShow(false);
            onDeleted?.(applicationId);
        } catch (err) {
            setError(
                err.response?.data?.message ||
                    'The draft could not be deleted. Please try again.'
            );
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            <Button
                variant="outline-danger"
                size={size}
                className={className}
                onClick={() => setShow(true)}
                aria-label={`Delete ${courseLabel} draft`}
            >
                Delete draft
            </Button>
            <Modal show={show} onHide={close} aria-labelledby={titleId}>
                <Modal.Header closeButton>
                    <Modal.Title id={titleId} as="h2" className="h5">
                        Delete this draft?
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-0">
                        Your unsubmitted application for {courseLabel} will be
                        deleted. This can&apos;t be undone.
                    </p>
                    {error && (
                        <Alert variant="danger" className="mt-3 mb-0">
                            {error}
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <ActionBar
                        className="w-100"
                        secondary={
                            <Button
                                variant="outline-primary"
                                onClick={close}
                                disabled={deleting}
                            >
                                Keep draft
                            </Button>
                        }
                        primary={
                            <Button
                                variant="danger"
                                onClick={confirm}
                                disabled={deleting}
                            >
                                {deleting ? (
                                    <>
                                        <Spinner
                                            animation="border"
                                            size="sm"
                                            className="me-2"
                                            aria-hidden="true"
                                        />
                                        Deleting…
                                    </>
                                ) : (
                                    'Delete draft'
                                )}
                            </Button>
                        }
                    />
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default DeleteDraftButton;
